import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as PipelineTemplatesService from '../services/PipelineTemplatesService';
import { PipelineTemplate } from '../types';
import { notifyError, notifySuccess } from '../../../../components/PlatformNotification';

const DEFAULT_LIMIT = 10;

// Query keys
export const pipelineTemplatesKeys = {
    all: ['pipelineTemplates'] as const,
    lists: () => [...pipelineTemplatesKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...pipelineTemplatesKeys.lists(), { filters }] as const,
    details: () => [...pipelineTemplatesKeys.all, 'detail'] as const,
    detail: (id: string) => [...pipelineTemplatesKeys.details(), id] as const,
};

// Hooks
export const usePipelineTemplatesQuery = (filters: Record<string, string> = {}) => {
    return useInfiniteQuery({
        queryKey: pipelineTemplatesKeys.list(filters),
        queryFn: ({ pageParam }: { pageParam: string | null }) =>
            PipelineTemplatesService.fetchRecords({
                filter: filters,
                limit: DEFAULT_LIMIT,
                nextToken: pageParam,
            }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
    });
};

export const usePipelineTemplateQuery = (id: string) => {
    return useQuery({
        queryKey: pipelineTemplatesKeys.detail(id),
        queryFn: () => PipelineTemplatesService.fetch(id),
        enabled: !!id,
    });
};

export const useCreatePipelineTemplateMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: PipelineTemplatesService.add,
        onSuccess: () => {
            notifySuccess('Pipeline template created');
            queryClient.invalidateQueries({ queryKey: pipelineTemplatesKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdatePipelineTemplateMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, record }: { id: string; record: PipelineTemplate }) =>
            PipelineTemplatesService.update(id, record),
        onSuccess: (_, { id }) => {
            notifySuccess('Pipeline template updated');
            queryClient.invalidateQueries({ queryKey: pipelineTemplatesKeys.lists() });
            queryClient.invalidateQueries({ queryKey: pipelineTemplatesKeys.detail(id) });
        },
        onError: notifyError,
    });
};

export const useDeletePipelineTemplateMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: PipelineTemplatesService.deleteRecord,
        onSuccess: () => {
            notifySuccess('Pipeline template deleted');
            queryClient.invalidateQueries({ queryKey: pipelineTemplatesKeys.all });
        },
        onError: notifyError,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const usePipelineTemplatesFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
